/**
 * โครงเมนู Desktop ERP สำหรับ Phase 1
 *
 * กติกาที่ต้องรักษา:
 * - หน้าจอที่ยังไม่มี endpoint จริงต้องแสดงเป็น disabled/planned ห้ามทำให้ดูเหมือน
 *   ธุรกรรมที่ใช้งานได้ (docs/SCOPE.md — Phase 1 Definition of Done)
 * - รายการ Pull/Kanban ต้องไม่ enabled ใน Phase 1 (D-034, docs/TASKS.md M12) —
 *   **D-102 (Phase 2, `WO-P2-M7-006`) เปิดใช้งานจริงแล้ว** สองรายการในกลุ่ม
 *   "การผลิต" (`kanban-replenishment-policy`/`kanban-signals`) จึงเป็น `available`
 *   โดยตั้งใจ แยกกันตามสองโมดูลสิทธิ์ที่ D-102 แยกไว้ ไม่ใช่ข้อยกเว้นของกฎข้อนี้
 * - `moduleCode` ตรงกับ `module_code` ของ RBAC matrix เพื่อให้ gate ด้วย
 *   `GET /v1/me/permissions` ได้ตอนหน้าจอจริงมาถึง (milestone M10)
 */

import { can } from '../lib/permissions/client';
import type { PermissionAction, PermissionsState } from '../lib/permissions/types';
import { receiveSalesOrderSearch } from '../lib/sales/salesView';

export type NavAvailability =
  /** ใช้งานได้จริงใน build นี้ */
  | 'available'
  /** อยู่ใน Phase 1 แต่ยังไม่ได้ implement (docs/TASKS.md M1–M11) */
  | 'planned'
  /** อยู่นอก Phase 1 โดยเจตนา (docs/SCOPE.md — Phase 2+) */
  | 'phase-2';

export interface NavItem {
  readonly id: string;
  readonly label: string;
  readonly icon: string;
  readonly availability: NavAvailability;
  /** route จริง — มีได้เฉพาะรายการที่ `availability === 'available'` */
  readonly to?: string;
  readonly moduleCode?: string;
  readonly action?: PermissionAction;
  readonly note?: string;
  /**
   * ซ่อนจากทั้ง Sidebar และ `visibleNavGroups` แต่ยังอยู่ใน `NAV_GROUPS` จริง
   * (D-131, `manual-plan`) — route ของมันต้องยังเปิดอยู่ (`DESKTOP_MODULE_ROUTES`
   * อ่านจาก `NAV_GROUPS` ตรง ๆ ไม่ผ่านธงนี้) มีไว้เพื่อเข้าถึงได้ผ่านปุ่มบนหน้าอื่น
   * เท่านั้น ไม่ใช่ลิงก์เมนู — ต่างจาก `availability: 'planned'/'phase-2'` ซึ่งซ่อน
   * เพราะยังไม่มี endpoint จริง ตัวนี้มี endpoint จริงและ `availability: 'available'`
   * แต่ถูกถอดออกจากเมนูด้วยเหตุผลด้าน UX โดยตรง
   */
  readonly hidden?: boolean;
  /**
   * ชั้นที่สามของเมนู — dropdown ลูกใต้รายการนี้ (D-116 ข้อ 4, ครั้งแรกของระบบ)
   *
   * ลูกไม่อยู่ใน `allNavItems()` (flatMap แค่ `group.items` เหมือนเดิม — ตรึงด้วย
   * test) สองรูปที่พบตอนนี้:
   * - **path เดียวกับแม่** ต่างแค่ search param (`sales-orders` → `sales-orders-receive`)
   *   — ลูกได้สิทธิ์จากแม่เสมอ ไม่ต้องประกาศ `moduleCode`/`action` เอง และไม่อยู่ใน
   *   `DESKTOP_MODULE_ROUTES` ด้วย เพราะไม่ใช่ route ที่สองที่ต้องมี guard ของตัวเอง
   * - **path ต่างจากแม่** (`production-plan` → `manual-plan`, D-130) — ต้องประกาศ
   *   `moduleCode`/`action` ของตัวเอง (ตรวจด้วย `isNavItemVisible` เหมือนรายการ
   *   ระดับบนทุกประการ ดู `visibleNavGroups`) และ**ยังต้องอยู่ใน**
   *   `DESKTOP_MODULE_ROUTES` (ดู `routes.ts`) เพราะมันเป็น route จริงที่ต้องมี guard
   *   — ตั้งแต่ D-131 ลูกชนิดนี้ (`manual-plan`) ยังอยู่ที่นี่แต่ถูก `hidden: true`
   *   ด้วย เพราะไม่ควรมีลิงก์เมนูให้กดอีกต่อไป มีแค่ route ที่ยังเปิดอยู่
   *
   * โครงสร้างนี้ **ใช้ได้ทั่วไป ไม่ผูกกับใบสั่งขาย** — Forecast และใบสั่งซื้อจะได้
   * เมนูย่อยแบบเดียวกันในใบถัดไป
   */
  readonly children?: readonly NavItem[];
}

export interface NavGroup {
  readonly id: string;
  readonly title: string;
  readonly items: readonly NavItem[];
}

export const NAV_GROUPS: readonly NavGroup[] = [
  {
    id: 'home',
    title: 'หน้าแรก / งานของฉัน',
    items: [
      {
        id: 'home-overview',
        label: 'ภาพรวมระบบ',
        icon: '🏠',
        availability: 'available',
        to: '/',
      },
      {
        id: 'home-my-work',
        label: 'งานที่รอฉันดำเนินการ',
        icon: '✅',
        availability: 'planned',
        note: 'รอ workflow อนุมัติจาก M3/M5',
      },
    ],
  },
  {
    id: 'master-data',
    title: 'ข้อมูลหลัก',
    items: [
      {
        id: 'product-master',
        label: 'ข้อมูลสินค้า (Product Master)',
        icon: '🏷️',
        availability: 'available',
        // path เดียวกับที่ `routes.ts` ลงทะเบียนไว้แล้ว ห้ามมี path ที่สอง
        // ไม่งั้นเมนูกับ guard จะชี้คนละเส้นและหลุดกันได้
        to: '/master-data/products',
        moduleCode: 'PRODUCT_MASTER',
        action: 'VIEW',
        note: 'ดู/สร้างข้อมูลสินค้า — ส่งอนุมัติและอนุมัติยังเป็นงานถัดไปของ M3',
      },
      {
        id: 'bom-routing',
        label: 'BOM และ Routing',
        icon: '📐',
        availability: 'available',
        // path เดียวกับที่ `routes.ts` ลงทะเบียนไว้แล้ว (`BOM_ROUTING_PATH`)
        // ห้ามมี path ที่สอง ไม่งั้นเมนูกับ guard จะชี้คนละเส้นและหลุดกันได้
        to: '/master-data/bom-routing',
        moduleCode: 'BOM_ROUTING',
        action: 'VIEW',
        note: 'ดูสูตรและขั้นตอนการผลิตรายโรงงาน — single level เท่านั้น (docs/TASKS.md M4)',
      },
      // `WO-P2-M11-010` — Tool/Die Master (P2-11d, D-108 ข้อ 4 และ 5) — **หน้าจอ
      // สุดท้ายของ Phase 2**
      //
      // อยู่ **หลัง BOM/Routing ทันที** ไม่ใช่ท้ายกลุ่ม: เครื่องมือถูกอ้างจาก
      // Routing operation (D-015 stub ที่ `WO-P2-M11-009` เติมคอลัมน์ให้) — คนที่
      // เปิดเมนูนี้มักมาจากการเห็นเครื่องมือถูกอ้างในหน้า BOM/Routing แล้วอยากรู้
      // รายละเอียด ต่างจากทะเบียนผู้ขาย/ลูกค้าซึ่งเป็นคู่ค้าองค์กรที่ไม่เกี่ยวกับ
      // Routing เลย
      //
      // route ถูก gate ด้วย **`PRODUCT_MASTER` + VIEW** — โมดูลเดียวกับสินค้าและ
      // BOM/Routing ไม่ใช่โมดูลใหม่ (`router.py`: "No new module code" — ดูแล
      // เครื่องมือเป็นงานของคนกลุ่มเดียวกับที่ดูแลข้อมูลสินค้า) ส่วน CREATE
      // (ลงทะเบียนเครื่องมือใหม่) และ EDIT (แก้ไข/เพิ่มการเชื่อมโยง/บันทึกซ่อมบำรุง)
      // ถูกบังคับ **ข้างใน** `ToolRegistryScreen` ตรงที่ของมันคือปุ่มแต่ละปุ่ม ตรงกับ
      // ด่านจริงฝั่งเซิร์ฟเวอร์ทีละเส้น (list/get = VIEW, create/patch/link/
      // maintenance = EDIT หรือ CREATE ตามที่ router จริงประกาศ)
      {
        id: 'tool-registry',
        label: 'เครื่องมือ/แม่พิมพ์ (Tool/Die Master)',
        icon: '🛠️',
        availability: 'available',
        // path เดียวกับที่ `routes.ts` ลงทะเบียนไว้แล้ว (`TOOL_REGISTRY_PATH`)
        // ห้ามมี path ที่สอง ไม่งั้นเมนูกับ guard จะชี้คนละเส้นและหลุดกันได้
        to: '/master-data/tooling',
        moduleCode: 'PRODUCT_MASTER',
        action: 'VIEW',
        note: 'ทะเบียนเครื่องมือ/แม่พิมพ์ระดับองค์กร ไม่แยกตามโรงงาน — สองตัวนับชีวิต (สะสมทั้งชีวิต และนับตั้งแต่ซ่อมบำรุงล่าสุด) คำนวณสดจากใบยืนยันงานเสมอ ไม่มีค่าใดถูกเก็บไว้ล่วงหน้า (D-108 ข้อ 5)',
      },
      // `WO-P2-M1-002` — รายการแรกของ Phase 2 ที่เปิดใช้งานจริง (P2-1b, D-083)
      //
      // อยู่ในกลุ่ม "ข้อมูลหลัก" เพราะทะเบียนผู้ขายเป็น master ระดับองค์กรที่งาน
      // จัดซื้อ/รับเข้า/จ้างผลิตภายนอกอ้างถึง ไม่ใช่ธุรกรรมของโดเมนใดโดเมนหนึ่ง
      // — และต่างจากเพื่อนบ้านในกลุ่มนี้ตรงที่มัน **ไม่มีขอบเขตโรงงาน** เลย
      {
        id: 'supplier-master',
        label: 'ผู้ขาย (Supplier Master)',
        icon: '🤝',
        availability: 'available',
        // path เดียวกับที่ `routes.ts` ลงทะเบียนไว้แล้ว (`SUPPLIER_MASTER_PATH`)
        // ห้ามมี path ที่สอง ไม่งั้นเมนูกับ guard จะชี้คนละเส้นและหลุดกันได้
        to: '/master-data/suppliers',
        // โมดูล **ธุรกิจ** ปกติ ไม่ใช่กลุ่ม System Administration — D-060 จึง deny
        // ADMIN และแผนกได้รับมันผ่านตารางสิทธิ์เหมือนโมดูลธุรกิจตัวอื่นทุกตัว
        moduleCode: 'SUPPLIER_MASTER',
        // `action` คือสิทธิ์ที่ต้องมีเพื่อ **เปิดหน้าจอนี้** ตามกติกาเดียวกับทุก
        // รายการที่เปิดใช้งานแล้ว: `GET /v1/suppliers` ถูก gate ด้วย VIEW ฝั่ง
        // เซิร์ฟเวอร์ ส่วนการ **เพิ่ม** (CREATE) และ **แก้ไข** (EDIT) ถูกกันข้างใน
        // `SupplierScreen` ตรงที่ของมันคือปุ่มแต่ละปุ่ม
        action: 'VIEW',
        note: 'ทะเบียนผู้ขายระดับองค์กร ไม่แยกตามโรงงาน — เลิกใช้ผู้ขายคือเปลี่ยนสถานะ ไม่มีการลบ (D-083)',
      },
      // `WO-P2-M4-003` — ทะเบียนลูกค้า (P2-4a, D-089)
      //
      // อยู่ **ติดกับทะเบียนผู้ขายทันที** โดยตั้งใจ: ทั้งสองเป็นทะเบียนคู่ค้าระดับ
      // องค์กรชนิดเดียวกัน (ฝั่งซื้อและฝั่งขาย) มี business code ที่แก้ไม่ได้ สามสถานะ
      // เท่ากัน และไม่มีการลบเหมือนกัน — contract ของลูกค้าเองก็เขียนว่ามิเรอร์ของ
      // ผู้ขาย (D-089) การวางทั้งคู่ไว้ข้างกันทำให้ผู้ใช้เห็นว่าเป็นของชนิดเดียวกัน
      // แทนที่จะต้องค้นหาทีละที่
      //
      // เหมือนผู้ขาย: **ไม่มีขอบเขตโรงงาน** เลย ต่างจาก Sales Order (P2-4b) ซึ่งเป็น
      // ธุรกรรมที่ผูกกับโรงงานเดียวทุกใบ
      {
        id: 'customer-master',
        label: 'ลูกค้า (Customer Master)',
        icon: '🛍️',
        availability: 'available',
        // path เดียวกับที่ `routes.ts` ลงทะเบียนไว้แล้ว (`CUSTOMER_MASTER_PATH`)
        // ห้ามมี path ที่สอง ไม่งั้นเมนูกับ guard จะชี้คนละเส้นและหลุดกันได้
        to: '/master-data/customers',
        // โมดูล **ธุรกิจ** ปกติ ไม่ใช่กลุ่ม System Administration — D-060 จึง deny
        // ADMIN และแผนกได้รับมันผ่านตารางสิทธิ์เหมือนโมดูลธุรกิจตัวอื่นทุกตัว
        moduleCode: 'CUSTOMER_MASTER',
        // `action` คือสิทธิ์ที่ต้องมีเพื่อ **เปิดหน้าจอนี้** ด้วยเหตุผลชุดเดียวกับที่
        // รายการผู้ขายด้านบนเขียนไว้: `GET /v1/customers` ถูก gate ด้วย VIEW ฝั่ง
        // เซิร์ฟเวอร์ ส่วนการ **เพิ่ม** (CREATE) และ **แก้ไข** (EDIT) ถูกกันข้างใน
        // `CustomerScreen` ตรงที่ของมันคือปุ่มแต่ละปุ่ม
        action: 'VIEW',
        note: 'ทะเบียนลูกค้าระดับองค์กร ไม่แยกตามโรงงาน — เลิกค้าขายคือเปลี่ยนสถานะ ไม่มีการลบ (D-089)',
      },
    ],
  },
  // `WO-P2-M2-003` — **กลุ่มใหม่กลุ่มแรกของ Phase 2** (P2-2a, D-084)
  //
  // อยู่ **หลังข้อมูลหลักและก่อนคลัง** ตามลำดับที่งานเกิดขึ้นจริง: เปิดใบขอซื้อ →
  // ออกใบสั่งซื้อ → ของมาถึงแล้วรับเข้าสต๊อก เมนูทั้งชุดเรียงตามสายงานอยู่แล้ว
  // (ข้อมูลหลัก → คลัง → ผลิต → ตามสอบ → บัญชี → ระบบ) การแทรกจัดซื้อไว้ท้ายสุด
  // จะทำให้ผู้ใช้ต้องกระโดดข้ามกลุ่มไปมาเพื่อทำงานหนึ่งรอบ
  //
  // ต่างจากทะเบียนผู้ขายซึ่งอยู่ในกลุ่ม "ข้อมูลหลัก" โดยตั้งใจ: ผู้ขายเป็น
  // **ทะเบียน** ที่ทุกโดเมนอ้าง ส่วนใบขอซื้อเป็น **ธุรกรรม** ของงานจัดซื้อ และมี
  // ขอบเขตโรงงานของตัวเอง (D-004) ซึ่งทะเบียนผู้ขายไม่มีเลย
  //
  // `WO-P2-M2-004` เพิ่มใบสั่งซื้อ (P2-2b, D-085) เข้ามาเป็นรายการที่สอง ซึ่งปิด
  // flow จัดซื้อทั้งสาย: ขอซื้อ → อนุมัติ → ออกใบสั่งซื้อ → (งานรับเข้า P2-3 ต่อ)
  {
    id: 'procurement',
    title: 'จัดซื้อ',
    items: [
      {
        id: 'purchase-requisitions',
        label: 'ใบขอซื้อ (Purchase Requisition)',
        icon: '📝',
        availability: 'available',
        // path เดียวกับที่ `routes.ts` ลงทะเบียนไว้แล้ว (`PURCHASE_REQUISITIONS_PATH`)
        // ห้ามมี path ที่สอง ไม่งั้นเมนูกับ guard จะชี้คนละเส้นและหลุดกันได้
        to: '/procurement/purchase-requisitions',
        // โมดูล **ธุรกิจ** ปกติ ไม่ใช่กลุ่ม System Administration — D-060 จึง deny
        // ADMIN และแผนกได้รับมันผ่านตารางสิทธิ์เหมือนโมดูลธุรกิจตัวอื่นทุกตัว
        moduleCode: 'PROCUREMENT',
        // `action` คือสิทธิ์ที่ต้องมีเพื่อ **เปิดหน้าจอนี้** ตามกติกาเดียวกับทุก
        // รายการที่เปิดใช้งานแล้ว: `GET /v1/purchase-requisitions` ถูก gate ด้วย
        // VIEW ฝั่งเซิร์ฟเวอร์ ส่วนการ **เปิดใบ** (CREATE), **ส่งอนุมัติ/ยกเลิก**
        // (EDIT) และ **อนุมัติ** (APPROVE) ถูกกันข้างใน
        // `PurchaseRequisitionScreen` ตรงที่ของมันคือปุ่มแต่ละปุ่ม
        action: 'VIEW',
        note: 'เปิดใบขอซื้อและขับสถานะ ร่าง → ส่งอนุมัติ → อนุมัติ — ผู้อนุมัติต้องไม่ใช่ผู้เปิดใบ (D-010)',
      },
      // `WO-P2-M2-004` — รายการที่สองของกลุ่มนี้ และเป็นตัวที่ **ปิด flow จัดซื้อ**
      // (P2-2b, D-085)
      //
      // อยู่ **หลังใบขอซื้อ** ตามลำดับที่งานเกิดจริง: ขอซื้อ → อนุมัติ → ออกใบสั่งซื้อ
      // ใบสั่งซื้อกินใบขอซื้อที่อนุมัติแล้วเป็นวัตถุดิบ การวางมันไว้ก่อนจะเป็นเมนูที่
      // เปิดขึ้นมาแล้วยังไม่มีอะไรให้ทำ
      //
      // โมดูลเดียวกับใบขอซื้อ (`PROCUREMENT`) ไม่ใช่โมดูลใหม่ — เป็นงานจัดซื้อเหมือนกัน
      // และตารางสิทธิ์ก็ให้มาแถวเดียวกัน (D-085)
      {
        id: 'purchase-orders',
        label: 'ใบสั่งซื้อ (Purchase Order)',
        icon: '🧾',
        availability: 'available',
        // path เดียวกับที่ `routes.ts` ลงทะเบียนไว้แล้ว (`PURCHASE_ORDERS_PATH`)
        // ห้ามมี path ที่สอง ไม่งั้นเมนูกับ guard จะชี้คนละเส้นและหลุดกันได้
        to: '/procurement/purchase-orders',
        moduleCode: 'PROCUREMENT',
        // `action` คือสิทธิ์ที่ต้องมีเพื่อ **เปิดหน้าจอนี้** ตามกติกาเดียวกับทุก
        // รายการที่เปิดใช้งานแล้ว: `GET /v1/purchase-orders` ถูก gate ด้วย VIEW ฝั่ง
        // เซิร์ฟเวอร์ ส่วนการ **ออกใบ** (CREATE), **ส่ง/ยืนยัน/ยกเลิก** (EDIT) และ
        // **อนุมัติ** (APPROVE) ถูกกันข้างใน `PurchaseOrderScreen` ตรงที่ของมันคือ
        // ปุ่มแต่ละปุ่ม
        action: 'VIEW',
        note: 'รวมใบขอซื้อที่อนุมัติแล้วของผู้ขายรายเดียวเป็นใบสั่งซื้อ แล้วขับสถานะ อนุมัติ → ส่ง → ผู้ขายยืนยัน — ผู้ขายที่ถูกระงับรับคำสั่งซื้อไม่ได้ (D-083/D-085)',
      },
      // `WO-P2-M14-011` — ข้อยกเว้นการรับของ (D-168 B6), **รายการที่สามของกลุ่มนี้**
      //
      // อยู่ **หลังใบสั่งซื้อทันที** ตามลำดับที่งานเกิดจริง: ขอซื้อ → ออกใบสั่งซื้อ →
      // ของมารับเข้า → ของขาด/เกิน/ส่งช้าหลังปิดกลายเป็นคิวที่จัดซื้อต้องกลับมาทำงานต่อ
      // บนใบสั่งซื้อเดิม — คนละเรื่องกับหน้ารับเข้าสต๊อก (D-059) ซึ่งเป็นงานคลัง
      //
      // `GET /v1/receiving-exceptions`/`/summary` ถูก gate ด้วย `PROCUREMENT` + VIEW
      // เดียวกับสองรายการข้างบน ส่วนปุ่ม "อนุมัติ" ข้างในหน้าจอต้องการ `INVENTORY` +
      // APPROVE ซึ่งเป็น grant เดิมของ `POST /v1/receiving-discrepancies/{id}/approve`
      // (D-091) — คนละโมดูลกับที่เปิดหน้าจอนี้โดยเจตนา ไม่ใช่ความผิดพลาด
      {
        id: 'receiving-exceptions',
        label: 'ข้อยกเว้นการรับของ (Receiving Exceptions)',
        icon: '⚠️',
        availability: 'available',
        // path เดียวกับที่ `routes.ts` ลงทะเบียนไว้แล้ว (`RECEIVING_EXCEPTIONS_PATH`)
        // ห้ามมี path ที่สอง ไม่งั้นเมนูกับ guard จะชี้คนละเส้นและหลุดกันได้
        to: '/procurement/receiving-exceptions',
        moduleCode: 'PROCUREMENT',
        action: 'VIEW',
        note: 'คิวของขาด/เกิน และของที่มาถึงหลัง PO ถูกปิดแบบ short-close แล้ว พร้อมรายงานสรุปมูลค่า — อนุมัติเป็นเส้นเดิมของ receiving discrepancy (INVENTORY + APPROVE) ไม่ใช่สิทธิ์เดียวกับการเปิดหน้าจอนี้ (D-168 B6)',
      },
    ],
  },
  // `WO-P2-M4-004` — **กลุ่มใหม่กลุ่มที่สองของ Phase 2** (P2-4b, D-090)
  //
  // อยู่ **หลังจัดซื้อและก่อนคลัง** ตามลำดับที่งานเกิดขึ้นจริง เมนูทั้งชุดเรียงตาม
  // สายงานอยู่แล้ว และคำสั่งซื้อจากลูกค้าคือสิ่งที่ทำให้ของเคลื่อน: รับ Customer PO
  // → วางแผน/ผลิต → ของออกจากคลัง การวางกลุ่มขายไว้ท้ายสุด (หรือหลังการผลิต) จะ
  // ทำให้เมนูอ่านเหมือนว่าการขายเกิดขึ้นหลังจากผลิตของเสร็จแล้ว ซึ่งกลับหัวกลับหาง
  // กับ Push flow ที่ระบบนี้ใช้อยู่
  //
  // **กลุ่มของตัวเอง ไม่ใช่สมาชิกของกลุ่มที่มีอยู่** — งานขายไม่ใช่งานจัดซื้อที่
  // กลับด้าน (คนละแผนก คนละโมดูลสิทธิ์ `SALES`) และไม่ใช่งานคลัง แม้ปลายทางของมัน
  // จะเป็นของที่ออกจากคลังก็ตาม กลุ่มนี้จะโตต่อด้วย Forecast/MPS (P2-5) และ
  // Packing/Delivery (P2-9) ซึ่งเป็นงานของแผนกเดียวกัน
  //
  // ต่างจากทะเบียนลูกค้าซึ่งอยู่ในกลุ่ม "ข้อมูลหลัก" โดยตั้งใจ: ลูกค้าเป็น
  // **ทะเบียน** ระดับองค์กรที่ไม่มีขอบเขตโรงงานเลย ส่วนใบสั่งขายเป็น **ธุรกรรม**
  // ที่ผูกกับโรงงานเดียวทุกใบ (D-004) — `navigation.ts` ของ `WO-P2-M4-003` เขียน
  // ความต่างข้อนี้ล่วงหน้าไว้แล้วตอนวางทะเบียนลูกค้า
  {
    id: 'sales',
    title: 'ขาย',
    items: [
      {
        id: 'sales-orders',
        label: 'ใบสั่งขาย (Sales Order)',
        icon: '🛒',
        availability: 'available',
        // path เดียวกับที่ `routes.ts` ลงทะเบียนไว้แล้ว (`SALES_ORDERS_PATH`)
        // ห้ามมี path ที่สอง ไม่งั้นเมนูกับ guard จะชี้คนละเส้นและหลุดกันได้
        to: '/sales/sales-orders',
        // โมดูล **ธุรกิจ** ปกติ ไม่ใช่กลุ่ม System Administration — D-060 จึง deny
        // ADMIN และแผนกได้รับมันผ่านตารางสิทธิ์เหมือนโมดูลธุรกิจตัวอื่นทุกตัว
        moduleCode: 'SALES',
        // `action` คือสิทธิ์ที่ต้องมีเพื่อ **เปิดหน้าจอนี้** ไม่ใช่สิทธิ์สูงสุดที่ทำ
        // อะไรได้บนหน้าจอ — ด้วยเหตุผลสามข้อเดียวกับที่รายการรับเข้าสต๊อกด้านล่าง
        // เขียนไว้ยาว ๆ: (1) `GET /v1/sales-orders` ถูก gate ด้วย VIEW ฝั่ง
        // เซิร์ฟเวอร์ guard ที่แรงกว่าจะซ่อนรายการที่ API ยินดีจะตอบอยู่แล้ว
        // (2) AUDITOR เป็น VIEW-only บนทุกโมดูลธุรกิจโดยการออกแบบ (D-009) และ
        // คำสั่งซื้อที่รับเข้ามาคือต้นทางของรายได้ที่ผู้ตรวจสอบต้องอ่านได้ และ
        // (3) สิทธิ์ที่แรงกว่าถูกบังคับที่หน้าจอเสมอในแอปนี้ — CREATE ที่ปุ่ม
        // "รับใบสั่งซื้อของลูกค้า" และ EDIT ที่ปุ่มยืนยัน/ยกเลิกรายแถว ข้างใน
        // `SalesOrderScreen`
        action: 'VIEW',
        note: 'รับใบสั่งซื้อของลูกค้าเข้าเป็นใบสั่งขาย แล้วขับสถานะ ร่าง → ยืนยันแล้ว — ผู้ยืนยันต้องไม่ใช่ผู้เปิดใบ (D-010) และคู่ (ลูกค้า, เลข PO) รับเข้าได้ครั้งเดียว (D-090)',
        // `WO-P2-M4-008` (D-116 ข้อ 4 และ 6) — ชั้นที่สามของเมนู ครั้งแรกของระบบ
        //
        // path เดียวกับแม่เป๊ะ (`/sales/sales-orders`) ต่าง search param เดียว
        // (`?view=receive`) — ไม่ใช่ route ที่สอง (`routes.ts` ไม่มีเส้นใหม่)
        // **ลูกได้สิทธิ์จากแม่**: SALES + VIEW เปิดหน้าได้ (ด่านเดียวกับแม่) ส่วน
        // CREATE ถูกบังคับที่ปุ่มส่งข้างใน `SalesOrderReceivePage` เหมือนทุกปุ่ม
        // เขียนอื่นในแอปนี้
        children: [
          {
            id: 'sales-orders-receive',
            label: 'รับใบสั่งซื้อของลูกค้า',
            icon: '📨',
            availability: 'available',
            to: `/sales/sales-orders${receiveSalesOrderSearch()}`,
            note: 'ย้ายมาจากปุ่มบนหน้ารายการ — ตอนนี้เป็นหน้า drill-down เต็มหน้าแทน Modal (D-116 ข้อ 3 และ 6)',
          },
        ],
      },
      // `WO-P2-M9-006` — Packing (P2-9f, D-105), **รายการที่สองของกลุ่มนี้**
      //
      // อยู่ **หลังใบสั่งขายทันที** ตามลำดับที่งานเกิดจริง: รับ Customer PO →
      // (วางแผน/ผลิต) → แพ็กของต่อบรรทัด → ส่งของ ครึ่งหลัง (Delivery/Dispatch/
      // Reversal) เป็นงานของ `WO-P2-M9-007` ซึ่งจะเป็นรายการที่สามของกลุ่มนี้
      //
      // gate ด้วย `SALES` เดียวกับใบสั่งขาย ไม่ใช่โมดูลใหม่: Packing เป็นงาน
      // ปลายทางของแผนกขายเดียวกัน (D-105) ไม่ใช่งานคลัง แม้ปลายทางของมันจะเป็น
      // ของที่ออกจากคลังก็ตาม
      {
        id: 'packing-units',
        label: 'แพ็กสินค้า (Packing)',
        icon: '📦',
        availability: 'available',
        // path เดียวกับที่ `routes.ts` ลงทะเบียนไว้แล้ว (`PACKING_UNITS_PATH`)
        // ห้ามมี path ที่สอง ไม่งั้นเมนูกับ guard จะชี้คนละเส้นและหลุดกันได้
        to: '/sales/packing',
        moduleCode: 'SALES',
        // `action` คือสิทธิ์ที่ต้องมีเพื่อ **เปิดหน้าจอนี้** ไม่ใช่สิทธิ์สูงสุดที่ทำ
        // อะไรได้บนหน้าจอ ตามกติกาเดียวกับทุกรายการที่เปิดใช้งานแล้ว:
        // `GET /v1/packing-units` ถูก gate ด้วย VIEW ฝั่งเซิร์ฟเวอร์ ส่วนการ
        // **เปิดกล่อง** (CREATE) และ **ยืนยัน/เปิดกล่องใหม่** (EDIT) ถูกบังคับ
        // **ข้างใน** `PackingScreen` ตรงที่ของมันคือปุ่มแต่ละปุ่ม — ตรงกับด่านจริง
        // ฝั่งเซิร์ฟเวอร์ทีละเส้น (list/get = VIEW, create = CREATE,
        // confirm/unpack = EDIT)
        action: 'VIEW',
        note: 'เปิดกล่องต่อบรรทัดใบสั่งขายพร้อม lot ยืนยันปิดกล่อง — แก้กล่องที่ปิดแล้วต้องเปิดกล่องใหม่เท่านั้น ไม่มีเส้นทางแก้จำนวนตรง ๆ (D-105)',
      },
      // `WO-P2-M9-007` — Delivery (P2-9g, D-105), **รายการที่สามของกลุ่มนี้**
      //
      // อยู่ **หลัง Packing ทันที** ตามลำดับที่งานเกิดจริง: แพ็กของ → โหลดขึ้นรถ →
      // สแกนออกจากประตู → (ถ้าผิด) ถอนการขาย ครึ่งแรก (Packing) เป็นงานของ
      // `WO-P2-M9-006` ซึ่งเป็นรายการก่อนหน้านี้ทันที
      //
      // gate ด้วย `SALES` เดียวกับใบสั่งขายและ Packing ไม่ใช่โมดูลใหม่: Delivery
      // เป็นครึ่งหลังของงานปลายทางแผนกขายเดียวกัน (D-105)
      {
        id: 'deliveries',
        label: 'ส่งของ (Delivery)',
        icon: '🚚',
        availability: 'available',
        // path เดียวกับที่ `routes.ts` ลงทะเบียนไว้แล้ว (`DELIVERIES_PATH`)
        // ห้ามมี path ที่สอง ไม่งั้นเมนูกับ guard จะชี้คนละเส้นและหลุดกันได้
        to: '/sales/deliveries',
        moduleCode: 'SALES',
        // `action` คือสิทธิ์ที่ต้องมีเพื่อ **เปิดหน้าจอนี้** ไม่ใช่สิทธิ์สูงสุดที่ทำ
        // อะไรได้บนหน้าจอ ตามกติกาเดียวกับ Packing: `GET /v1/packing-units` และ
        // `GET /v1/deliveries` ถูก gate ด้วย VIEW ฝั่งเซิร์ฟเวอร์ ส่วน **โหลดขึ้นรถ**
        // (EDIT), **ส่งออกจากประตู** (CREATE) และ **ถอนการขาย** (APPROVE) ถูกบังคับ
        // **ข้างใน** `DeliveryScreen` ตรงที่ของมันคือปุ่มแต่ละปุ่ม — ตรงกับด่านจริง
        // ฝั่งเซิร์ฟเวอร์ทีละเส้น
        action: 'VIEW',
        note: 'โหลดกล่องขึ้นรถ สแกนออกจากประตู และถอนการขายเมื่อส่งของผิด — คนที่ส่งของออกไปได้ (CREATE) ไม่ได้แปลว่าถอนการขายได้ (APPROVE) — D-105 ข้อ 6',
      },
    ],
  },
  // `WO-P2-M5-003` — **กลุ่มใหม่กลุ่มที่สามของ Phase 2** (P2-5a, D-093)
  //
  // อยู่ **หลังขายและก่อนคลัง** ตามลำดับที่งานเกิดขึ้นจริง เมนูทั้งชุดเรียงตามสายงาน
  // อยู่แล้ว และการวางแผนคือสิ่งที่คั่นอยู่ระหว่าง **ความต้องการที่เข้ามา** กับ
  // **ของที่เคลื่อน**: รับ Forecast/Customer PO → วางแผนว่าจะผลิต/ซื้ออะไรเมื่อไร →
  // ของเข้าออกคลัง การวางกลุ่มวางแผนไว้หลังคลังหรือหลังการผลิตจะอ่านเหมือนว่าเรา
  // วางแผนหลังจากของเคลื่อนไปแล้ว ซึ่งกลับหัวกลับหางกับ Push flow ที่ระบบนี้ใช้
  //
  // **กลุ่มของตัวเอง ไม่ใช่สมาชิกของกลุ่ม "ขาย"** แม้ Forecast จะมาจากลูกค้าเหมือน
  // Customer PO: งานวางแผนไม่ใช่งานขาย (คนละแผนก คนละโมดูลสิทธิ์ `PLANNING` ซึ่ง
  // D-093 สร้างขึ้นใหม่โดยตรงเพราะ Forecast/MPS/MRP ไม่ใช่ทั้งการขายและการซื้อ)
  // — หมายเหตุของกลุ่ม `sales` ด้านบนเคยเขียนล่วงหน้าไว้ว่ากลุ่มนั้นจะโตต่อด้วย
  // Forecast/MPS (P2-5) แต่การตัดสินใจตอน D-093 ให้ `PLANNING` เป็น **โมดูลสิทธิ์
  // ของตัวเอง** ทำให้การใส่ไว้ในกลุ่ม "ขาย" กลายเป็นกลุ่มที่สมาชิกสองรายการปลดล็อก
  // ด้วยสิทธิ์คนละชุด ซึ่งผู้ใช้จะเห็นกลุ่มที่มีรายการเดียวเสมอโดยไม่รู้ว่าทำไม
  //
  // กลุ่มนี้จะโตต่อด้วย Net Forecast (P2-5b), MPS (P2-5c) และ Capacity/MRP (P2-6)
  // ซึ่ง D-093 ระบุไว้แล้วว่าอยู่ในโมดูล `PLANNING` เดียวกันนี้
  {
    id: 'planning',
    title: 'วางแผน',
    items: [
      {
        id: 'forecast-imports',
        label: 'Forecast ลูกค้า (Forecast Import)',
        icon: '📈',
        availability: 'available',
        // path เดียวกับที่ `routes.ts` ลงทะเบียนไว้แล้ว (`FORECAST_IMPORTS_PATH`)
        // ห้ามมี path ที่สอง ไม่งั้นเมนูกับ guard จะชี้คนละเส้นและหลุดกันได้
        to: '/planning/forecast-imports',
        // โมดูล **ธุรกิจ** ปกติ ไม่ใช่กลุ่ม System Administration — D-060 จึง deny
        // ADMIN และแผนกวางแผนได้รับมันผ่านตารางสิทธิ์เหมือนโมดูลธุรกิจตัวอื่นทุกตัว
        moduleCode: 'PLANNING',
        // `action` คือสิทธิ์ที่ต้องมีเพื่อ **เปิดหน้าจอนี้** ไม่ใช่สิทธิ์สูงสุดที่ทำ
        // อะไรได้บนหน้าจอ — ด้วยเหตุผลสามข้อเดียวกับที่รายการรับเข้าสต๊อกด้านล่าง
        // เขียนไว้ยาว ๆ: (1) `GET /v1/forecast-imports` ถูก gate ด้วย VIEW ฝั่ง
        // เซิร์ฟเวอร์ guard ที่แรงกว่าจะซ่อนรายการที่ API ยินดีจะตอบอยู่แล้ว
        // (2) AUDITOR เป็น VIEW-only บนทุกโมดูลธุรกิจโดยการออกแบบ (D-009) และแผน
        // ความต้องการที่ระบบใช้ตั้งต้นการผลิตคือสิ่งที่ผู้ตรวจสอบต้องอ่านได้ และ
        // (3) สิทธิ์ที่แรงกว่าถูกบังคับที่หน้าจอเสมอในแอปนี้ — CREATE ที่ปุ่ม
        // "นำ Forecast เข้าระบบ" และ **APPROVE** ที่ปุ่ม "ประกาศใช้" รายแถว ข้างใน
        // `ForecastImportScreen`
        action: 'VIEW',
        note: 'นำแผนความต้องการรายวันของลูกค้าเข้าระบบ ตรวจทีละบรรทัด แล้วจึงประกาศใช้ — ไฟล์ที่มีบรรทัดผิดถูกเก็บไว้ทั้งชุดให้เห็นว่าแถวไหนผิด และฉบับที่ถูกแทนแล้วยังอ่านได้ (D-093)',
      },
      // `WO-P2-M6-006` — รัน MRP (P2-6e, D-100/D-101), **รายการที่สองของกลุ่มนี้**
      //
      // อยู่ **หลัง Forecast ทันที** ตามลำดับที่งานเกิดจริง: Forecast → MPS (ยัง
      // ไม่มีหน้าจอ) → MRP รันต่อแผนที่ปล่อยแล้ว — MRP จึงเป็นปลายทางสุดท้ายของสาย
      // วางแผนที่มีหน้าจอแล้ว ไม่ใช่กลุ่มใหม่: gate ด้วยโมดูล `PLANNING` เดียวกับ
      // Forecast เพราะเป็นงานของฝ่ายวางแผนแผนกเดียวกัน (D-093)
      {
        id: 'mrp-runs',
        label: 'รัน MRP (MRP Run)',
        icon: '🧮',
        availability: 'available',
        // path เดียวกับที่ `routes.ts` ลงทะเบียนไว้แล้ว (`MRP_RUNS_PATH`)
        // ห้ามมี path ที่สอง ไม่งั้นเมนูกับ guard จะชี้คนละเส้นและหลุดกันได้
        to: '/planning/mrp-runs',
        moduleCode: 'PLANNING',
        // `action` คือสิทธิ์ที่ต้องมีเพื่อ **เปิดหน้าจอนี้** ไม่ใช่สิทธิ์สูงสุดที่ทำ
        // อะไรได้บนหน้าจอ — ด้วยเหตุผลสามข้อเดียวกับที่ Forecast ด้านบนเขียนไว้ยาว ๆ:
        // (1) `GET /v1/mrp-runs` ถูก gate ด้วย VIEW ฝั่งเซิร์ฟเวอร์ guard ที่แรงกว่า
        // จะซ่อนรายการที่ API ยินดีจะตอบอยู่แล้ว (2) AUDITOR เป็น VIEW-only บนทุก
        // โมดูลธุรกิจโดยการออกแบบ (D-009) และแผนที่ระบบเสนอให้ซื้อ/สร้างคือสิ่งที่
        // ผู้ตรวจสอบต้องอ่านได้ และ (3) สิทธิ์ที่แรงกว่าถูกบังคับที่หน้าจอเสมอในแอปนี้
        // — CREATE ที่ปุ่ม "เริ่มรัน MRP" และปุ่ม "ปล่อยเป็นเอกสารจริง" รายรัน ข้างใน
        // `MrpRunScreen` (ทั้งสองปุ่มถือ grant เดียวกันตามที่ router จริงประกาศ —
        // D-101: การปล่อยไม่ต้องการ APPROVE เพิ่ม เพราะทุกเอกสารที่มันสร้างเป็น
        // DRAFT เสมอ)
        action: 'VIEW',
        note: 'รัน MRP ต่อแผน MPS ที่ปล่อยแล้ว อ่านสิ่งที่รันเสนอ (planned order และชิ้นส่วนที่งดเว้นเพราะมีนโยบายเติมสินค้าอัตโนมัติอยู่แล้ว) แล้วจึงปล่อยเป็นใบสั่งผลิต/ใบขอซื้อร่างอย่างจงใจ — สองขั้นตอนที่แยกกันเสมอ ไม่มีการปล่อยอัตโนมัติต่อจากการรัน (D-100/D-101)',
      },
      // `WO-P2-M5-006` — แผนการผลิต (P2-5 ต่อยอด D-110 ข้อ 8), D-127
      //
      // อยู่หลัง MRP ทันที: ใช้ GET /v1/mps-versions + GET /v1/mps-attainment ที่มี
      // อยู่แล้วทั้งคู่ gate ด้วย PLANNING + VIEW เดียวกับทุกรายการของกลุ่มนี้
      {
        id: 'production-plan',
        label: 'แผนการผลิต (Production Plan)',
        icon: '📋',
        availability: 'available',
        to: '/planning/production-plan',
        moduleCode: 'PLANNING',
        action: 'VIEW',
        note: 'แสดงรายการที่ต้องผลิตจากแผน MPS รายวันเทียบยอดที่ผลิตได้จริง — คงเหลือคำนวณที่หน้าจอ ไม่เก็บ และปุ่มสร้างใบสั่งผลิตเป็นลิงก์พรีฟิลไปหน้าสร้างเดิม ไม่ใช่การสร้างอัตโนมัติ (D-127)',
        // `WO-P2-M5-012` (D-130) — ย้าย "วางแผนผลิตแบบ Manual" (D-129) มาเป็นลูกของ
        // รายการนี้ จาก top-level เดิม — คนละหน้ากับแม่ (แม่อ่านเทียบผลจริงอย่างเดียว
        // ไม่มีปุ่มเขียน ส่วนลูกคือที่เดียวที่ `POST /v1/mps-versions` ถูกเรียกจากเว็บ)
        // **path ของลูกต่างจากแม่** (`/planning/manual-plan` ไม่ใช่ search param ของ
        // `/planning/production-plan`) ต่างจากลูกของ `sales-orders` — จึงยังต้องมี
        // route guard ของตัวเองใน `DESKTOP_MODULE_ROUTES` (ดู `routes.ts`) และ
        // ประกาศ `moduleCode`/`action` ไว้ชัด ๆ บนตัวเอง ไม่พึ่งการสืบทอดจากแม่
        //
        // `WO-P2-M5-014` (D-131 แทนที่ D-130) — ลูกนี้ถูก `hidden: true` แล้ว: เข้า
        // หน้านี้ผ่านปุ่มบนหน้าแผนการผลิต (`WO-P2-M5-013`) เท่านั้น ไม่ใช่ลิงก์เมนู
        // อีกต่อไป — ยังอยู่ใน `children` ที่นี่เพื่อให้ route/guard (ข้างบน) และ
        // `DESKTOP_MODULE_ROUTES` ไม่ต้องเปลี่ยนแปลง (ยัง flatMap อ่านจาก `NAV_GROUPS`
        // ตรง ๆ ไม่ผ่านธง `hidden`)
        children: [
          {
            id: 'manual-plan',
            label: 'แผนการผลิตแบบ manual',
            icon: '📝',
            availability: 'available',
            to: '/planning/manual-plan',
            moduleCode: 'PLANNING',
            action: 'VIEW',
            hidden: true,
            note: 'สร้างแผน MPS ด้วยมือ: โรงงาน ชื่อเวอร์ชัน ช่วงวางแผน และบรรทัดสินค้า×วัน×จำนวน — บันทึกเป็นร่าง (DRAFT) เสมอ ต้องให้ผู้อื่นอนุมัติต่อ ปรับแผนทำโดยสร้างเวอร์ชันใหม่ ไม่ใช่แก้ทับ (D-129)',
          },
        ],
      },
    ],
  },
  {
    id: 'inventory',
    title: 'คลังและสต๊อก',
    items: [
      {
        id: 'goods-receipt',
        label: 'รับเข้าสต๊อก (Goods Receipt)',
        icon: '📥',
        availability: 'available',
        // path เดียวกับที่ `routes.ts` ลงทะเบียนไว้แล้ว ห้ามมี path ที่สอง
        // ไม่งั้นเมนูกับ guard จะชี้คนละเส้นและหลุดกันได้
        to: '/inventory/goods-receipt',
        moduleCode: 'INVENTORY',
        // `action` คือสิทธิ์ที่ต้องมีเพื่อ **เปิดหน้าจอนี้** ไม่ใช่สิทธิ์สูงสุดที่ทำ
        // อะไรได้บนหน้าจอ — `routes.ts` อ่านค่านี้ไปเป็น guard ของ route ตรง ๆ
        // ค่านี้จึงเป็น VIEW เท่ากับทุกรายการที่เปิดใช้งานแล้ว (`product-master`,
        // `bom-routing`, `production-orders`) ส่วนการ **รับของเข้า** ถูกกันด้วย
        // `INVENTORY` + CREATE ข้างใน `GoodsReceiptScreen` ซึ่งเป็นที่ของมัน
        //
        // สามเหตุผลที่ไม่ใช่ CREATE (ตัดสินโดย `erp-orchestrator`):
        //  1. `GET /v1/goods-receipts` ถูก gate ด้วย VIEW ฝั่งเซิร์ฟเวอร์ — guard
        //     ที่ CREATE จะซ่อนรายการที่ API ยินดีจะตอบอยู่แล้ว หน้าจอเข้มกว่า
        //     สิ่งที่มันห่ออยู่โดยไม่ได้ปกป้องอะไรเพิ่ม
        //  2. AUDITOR เป็น VIEW-only บนทุกโมดูลธุรกิจโดยการออกแบบ (D-009) การรับ
        //     เข้าหนึ่งใบสร้างเอกสารบัญชีและความเคลื่อนไหวสต๊อกเป็นบวก ผู้ตรวจสอบที่
        //     เปิดสมุดรับเข้าไม่ได้ จึงตรวจสต๊อกและการตีมูลค่าไม่ได้เลย — CREATE
        //     ตรงนี้ทำลายเหตุผลของบทบาทนั้น ไม่ได้ป้องกันอะไร
        //  3. สิทธิ์ที่แรงกว่าถูกบังคับที่หน้าจอเสมอในแอปนี้ การมีข้อยกเว้นหนึ่งข้อ
        //     คือกติกาที่ต้องจำเป็นราย ๆ ไป แทนที่จะอ่านออกจากโครงเดียวกันทั้งหมด
        action: 'VIEW',
        note: 'รับของเข้าสต๊อกและดูรายการที่รับเข้าแล้ว — ของที่รับเข้าใหม่อยู่ในสถานะกักรอตรวจเสมอ (D-059)',
      },
      // `WO-P2-M3-003` — กฎตรวจรับเข้า (P2-3a, D-086)
      //
      // อยู่ **หลังรับเข้าสต๊อก** ตามความสัมพันธ์ที่แท้จริงของทั้งสอง: หน้าจอรับเข้า
      // คือที่ที่ของมาถึงจริง ส่วนหน้านี้คือที่ที่ตัดสินล่วงหน้าว่าของของคู่ไหนจะถูก
      // กักรอตรวจเมื่อมันมาถึง — คนที่เปิดเมนูนี้มาเพราะเพิ่งเห็นของถูกกักในหน้าแรก
      //
      // อยู่ในกลุ่ม "คลังและสต๊อก" ไม่ใช่ "ข้อมูลหลัก" แม้จะเป็นตารางตั้งค่า: สิ่งที่
      // มันควบคุมคือสถานะของสต๊อกที่กำลังรับเข้า และตารางสิทธิ์ก็ให้มันมาพร้อมโมดูล
      // `INVENTORY` เดียวกับงานรับเข้า ไม่ใช่โมดูลข้อมูลหลัก
      {
        id: 'incoming-inspection-rules',
        label: 'กฎตรวจรับเข้า (Incoming Inspection Rule)',
        icon: '🔍',
        availability: 'available',
        // path เดียวกับที่ `routes.ts` ลงทะเบียนไว้แล้ว (`INCOMING_INSPECTION_RULES_PATH`)
        // ห้ามมี path ที่สอง ไม่งั้นเมนูกับ guard จะชี้คนละเส้นและหลุดกันได้
        to: '/inventory/incoming-inspection-rules',
        // **`INVENTORY` เดิม ไม่ใช่โมดูลใหม่ชื่อ `QUALITY`** (D-086) — กฎนี้กำหนด
        // สถานะคุณภาพของสต๊อกที่กำลังรับเข้า จึงเป็นสิทธิ์ชุดเดียวกับที่คนคลังและคน
        // รับของถืออยู่แล้ว โมดูลที่สองตรงนี้จะเป็นเมนูที่ไม่มีแผนกไหนถือได้เลย
        moduleCode: 'INVENTORY',
        // `action` คือสิทธิ์ที่ต้องมีเพื่อ **เปิดหน้าจอนี้** ด้วยเหตุผลสามข้อเดียวกัน
        // กับที่รายการรับเข้าสต๊อกด้านบนเขียนไว้ยาว ๆ: (1) `GET` ของกลุ่มนี้ถูก gate
        // ด้วย VIEW ฝั่งเซิร์ฟเวอร์ guard ที่แรงกว่าจะซ่อนรายการที่ API ยินดีจะตอบ
        // (2) AUDITOR เป็น VIEW-only บนทุกโมดูลธุรกิจ (D-009) และกฎที่ตัดสินว่าของ
        // ล็อตไหนถูกกักไว้คือสิ่งที่ผู้ตรวจสอบต้องอ่านได้ และ (3) สิทธิ์ที่แรงกว่าถูก
        // บังคับที่หน้าจอเสมอในแอปนี้ — CREATE ที่ปุ่ม "ตั้งกฎใหม่" และ EDIT ที่ปุ่ม
        // "แก้ไข" รายแถว ข้างใน `IncomingInspectionRuleScreen`
        action: 'VIEW',
        note: 'ตั้งว่าของที่รับเข้าของแต่ละคู่สินค้า+ผู้ขายต้องกักรอตรวจหรือไม่ — กฎระดับองค์กร ไม่แยกตามโรงงาน และคู่ที่ยังไม่มีกฎถือว่าต้องตรวจ (D-086)',
      },
      // `WO-P2-M11-008` — เคส NCR (P2-11b, D-108 ข้อ 2 และ 3), **รายการที่สามของ
      // กลุ่มนี้ที่ใช้งานได้จริง**
      //
      // อยู่ **หลังกฎตรวจรับเข้าทันที**: ทั้งสองเป็นงานคุณภาพของสต๊อกที่กำลังรับเข้า
      // หรืออยู่ระหว่างผลิต ไม่ใช่หน้าจอตั้งค่าที่แยกขาดจากกัน — คนที่เปิดเมนูนี้มาเพราะ
      // เพิ่งเห็นล็อตถูกกักในหน้าอื่นแล้วต้องการรู้ว่าทำไม
      //
      // route ถูก gate ด้วย **`INVENTORY` + VIEW** — โมดูลเดียวกับกฎตรวจรับเข้าและการ
      // รับเข้าสต๊อก ไม่ใช่โมดูลใหม่ชื่อ `QUALITY` (D-108 ยึดตามแบบที่ D-086 วางไว้)
      // ส่วน CREATE (เปิดเคส), EDIT (สอบสวน) และ APPROVE (ปิดเคส — แรงที่สุดเพราะ
      // ย้ายสถานะคุณภาพของล็อตจริง) ถูกบังคับ **ข้างใน** `NcrCaseScreen` ตรงที่ของมัน
      // คือปุ่มแต่ละปุ่ม ซึ่งตรงกับด่านจริงฝั่งเซิร์ฟเวอร์ทีละเส้น (list/get = VIEW,
      // create = CREATE, investigate = EDIT, close = APPROVE)
      {
        id: 'ncr-cases',
        label: 'เคส NCR (Non-Conformance Report)',
        icon: '🚫',
        availability: 'available',
        // path เดียวกับที่ `routes.ts` ลงทะเบียนไว้แล้ว (`NCR_CASES_PATH`)
        // ห้ามมี path ที่สอง ไม่งั้นเมนูกับ guard จะชี้คนละเส้นและหลุดกันได้
        to: '/inventory/ncr-cases',
        moduleCode: 'INVENTORY',
        action: 'VIEW',
        note: 'รายงานปัญหาที่พบในล็อต/ใบรับเข้า/ใบยืนยันงาน สอบสวน แล้วปิดเคสด้วยการตัดสินใจ — ปิดเคสคือการกระทำเดียวที่ย้ายสถานะคุณภาพของล็อต และเคสที่ปิดแล้วแก้ไม่ได้อีก ต้องยกเคสใหม่แทน (D-108)',
      },
      {
        id: 'initial-stock-import',
        label: 'นำเข้าสต๊อกตั้งต้น',
        icon: '🗃️',
        availability: 'planned',
        moduleCode: 'INVENTORY',
        action: 'CREATE',
        note: 'docs/TASKS.md M4b — D-032',
      },
      {
        id: 'inventory-ledger',
        label: 'ความเคลื่อนไหวสต๊อก',
        icon: '📒',
        availability: 'planned',
        moduleCode: 'INVENTORY',
        action: 'VIEW',
        note: 'docs/TASKS.md M4a',
      },
      {
        id: 'product-staging',
        label: 'จัดเตรียมวัตถุดิบ (Staging)',
        icon: '🧰',
        availability: 'planned',
        moduleCode: 'SHOP_FLOOR',
        action: 'CREATE',
        note: 'docs/TASKS.md M6 — D-027',
      },
    ],
  },
  {
    id: 'production',
    title: 'การผลิต',
    items: [
      {
        id: 'production-orders',
        label: 'ใบสั่งผลิต',
        icon: '⚙️',
        availability: 'available',
        // path เดียวกับที่ `routes.ts` ลงทะเบียนไว้แล้ว ห้ามมี path ที่สอง
        to: '/production/orders',
        moduleCode: 'PRODUCTION_ORDER',
        action: 'VIEW',
        note: 'ดู/เปิด/สั่งปล่อยใบสั่งผลิต — ปิดงานและปิดใบยังเป็นงานถัดไปของ M5/M6',
      },
      // `WO-P2-M16-001` — ป้าย Kanban การผลิต (D-169, D-170) — **อยู่หลังใบสั่งผลิต
      // ทันที** ตามลำดับที่งานเกิดจริง: ปล่อยใบสั่งผลิต → พิมพ์ป้าย Kanban ต่อ
      // operation → Production สแกนป้ายใบนั้นทำงาน
      //
      // gate ด้วย `PRODUCTION_ORDER` + **VIEW** เดียวกับใบสั่งผลิต ไม่ใช่โมดูลใหม่ —
      // ป้ายเป็นเอกสารของใบสั่งผลิตที่ปล่อยแล้ว ไม่ใช่ทรัพยากรแยก ส่วนปุ่ม "พิมพ์ป้าย"/
      // "พิมพ์ซ้ำ" ต้องการ **CREATE** ถูกบังคับ **ข้างใน** `OutputTagScreen` ตรงที่ของ
      // มันคือปุ่มรายแถว ตรงกับด่านจริงฝั่งเซิร์ฟเวอร์ (list/get = VIEW, print/reprint
      // = CREATE)
      {
        id: 'output-tags',
        label: 'ป้าย Kanban',
        icon: '🏷️',
        availability: 'available',
        // path เดียวกับที่ `routes.ts` ลงทะเบียนไว้แล้ว (`OUTPUT_TAGS_PATH`)
        // ห้ามมี path ที่สอง ไม่งั้นเมนูกับ guard จะชี้คนละเส้นและหลุดกันได้
        to: '/production/output-tags',
        moduleCode: 'PRODUCTION_ORDER',
        action: 'VIEW',
        note: 'เลือกใบสั่งผลิตที่ปล่อยแล้ว พิมพ์/พิมพ์ซ้ำป้ายต่อ operation และพรีวิว+พิมพ์ป้าย A6 แนวนอน (D-169, D-170)',
      },
      {
        id: 'shop-floor-scan',
        label: 'สแกนหน้างาน (Scan-In / Scan-Out)',
        icon: '📷',
        availability: 'planned',
        moduleCode: 'SHOP_FLOOR',
        action: 'CREATE',
        note: 'docs/TASKS.md M6 — ใช้งานจริงบนเครื่องสแกน Android',
      },
      {
        id: 'scanner-preview',
        label: 'มุมมองเครื่องสแกน Android',
        icon: '📱',
        availability: 'available',
        to: '/scanner',
        note: 'โครงหน้าจอเท่านั้น ยังไม่บันทึกธุรกรรม',
      },
      {
        id: 'offline-sync-conflicts',
        label: 'ข้อขัดแย้งจากการซิงก์เครื่องสแกน',
        icon: '⚠️',
        availability: 'available',
        // path เดียวกับที่ `routes.ts` ลงทะเบียนไว้แล้ว (`OFFLINE_CONFLICTS_PATH`)
        // ห้ามมี path ที่สอง ไม่งั้นเมนูกับ guard จะชี้คนละเส้นและหลุดกันได้
        to: '/production/offline-conflicts',
        // **`SHOP_FLOOR` ไม่ใช่โมดูลใหม่ชื่อ offline-sync** — Phase 1 ไม่มีโมดูลนั้น
        // ให้ department ใดถือได้ และ `modules/offline/schemas.py` ก็ประกาศตรง ๆ ว่า
        // `OFFLINE_SYNC_MODULE = SHOP_FLOOR_MODULE`: รายการในคิวคือคำสั่งหน้างานที่
        // ถูกปฏิเสธ จึงเป็นเรื่องของโมดูลเดียวกับที่ออกคำสั่งนั้นมา
        moduleCode: 'SHOP_FLOOR',
        // `action` คือสิทธิ์ที่ต้องมีเพื่อ **เปิดหน้าจอนี้** ตามกติกาเดียวกับทุก
        // รายการที่เปิดใช้งานแล้ว: `GET /v1/offline-sync/conflicts` ถูก gate ด้วย
        // VIEW ฝั่งเซิร์ฟเวอร์ ส่วนการ **บันทึกการตัดสิน** ถูกกันด้วย `SHOP_FLOOR` +
        // APPROVE ข้างใน `OfflineConflictsScreen` ซึ่งเป็นที่ของมัน (D-077)
        action: 'VIEW',
        // ⚠️ Mockup วางรายการนี้ไว้ในกลุ่ม "ระบบและสิทธิ์" แต่กลุ่มนั้นทั้งกลุ่มเป็น
        // ของโมดูล `RBAC_ADMIN` ในระบบจริง — รายการที่ gate ด้วย `SHOP_FLOOR` จึงอยู่
        // กับงานผลิตที่มันเกิดมาจาก การย้ายกลุ่มเป็นการเปลี่ยนโครงเมนูซึ่งเป็นข้อเสนอ
        // ให้ `erp-orchestrator` ไม่ใช่การตัดสินใจของ Work Order นี้
        note: 'คำสั่งจากเครื่องสแกนที่เซิร์ฟเวอร์ปฏิเสธ รอให้คนตัดสิน — ปิดรายการคือการบันทึกการตัดสิน ไม่ใช่การส่งคำสั่งเข้าระบบใหม่ (D-077)',
      },
      // `WO-P2-M7-006` (P2-7f, D-102) — เดิมเป็นรายการ `phase-2` เดียว ("เติมเต็มแบบ
      // Pull / Kanban") ตอนนี้ D-102 เปิดใช้งาน Pull จริงแล้ว และ**แยกเป็นสองรายการ
      // ตามสองโมดูลสิทธิ์ที่แยกกันโดยตั้งใจ** ไม่ใช่รายการเดียวที่พารามิเตอร์บอก
      // "Kanban ทั้งหมด": RBAC เป็น `module × action` ดังนั้น "ตั้ง policy" กับ
      // "สแกนเบิก" ต่างก็เป็น `CREATE` แยกด้วย action ไม่ได้ ต้องแยกด้วยโมดูล —
      // `KANBAN_CONFIG` (ตั้ง policy/control cycle/ออกการ์ด — ตัดสินกำลังการผลิต
      // ของไลน์) กับ `KANBAN_EXECUTION` (สแกนเบิก/รับทราบ/เติมของ — งานตามกติกา)
      // ทั้งสองไม่มีวันปรากฏบนหน้าจอเดียวกัน (ดู `desktop/kanban/*Screen.tsx`)
      {
        id: 'kanban-replenishment-policy',
        label: 'ตั้งค่า Kanban (Replenishment Policy)',
        icon: '🔄',
        availability: 'available',
        // path เดียวกับที่ `routes.ts` ลงทะเบียนไว้แล้ว
        // (`KANBAN_REPLENISHMENT_POLICIES_PATH`) ห้ามมี path ที่สอง
        to: '/production/kanban/replenishment-policies',
        moduleCode: 'KANBAN_CONFIG',
        action: 'VIEW',
        note: 'ตั้ง policy, ตัด control cycle และออกการ์ด — กำลังการผลิตของไลน์ที่ operator ต้องแตะไม่ได้ (D-102 ข้อ 1)',
      },
      {
        id: 'kanban-signals',
        label: 'บอร์ด Kanban (Signal)',
        icon: '📶',
        availability: 'available',
        // path เดียวกับที่ `routes.ts` ลงทะเบียนไว้แล้ว (`KANBAN_SIGNALS_PATH`)
        // ห้ามมี path ที่สอง
        to: '/production/kanban/signals',
        moduleCode: 'KANBAN_EXECUTION',
        action: 'VIEW',
        note: 'สแกนเบิก รับทราบ เริ่มเติม เติมของ และยกเลิกสัญญาณ — งานตามกติกาที่ policy วางไว้ ไม่ใช่การตั้งกติกาเอง (D-102 ข้อ 1)',
      },
    ],
  },
  // `WO-P2-M8-007` — **กลุ่มใหม่กลุ่มที่ห้าของ Phase 2** (P2-8g, D-103) — และเป็น
  // **หน้าจอสุดท้ายของ Phase 2 ที่ยังไม่มีหน้าจอ** ตามที่ Work Order นี้ระบุไว้ตรง ๆ
  //
  // อยู่ **หลังการผลิตและก่อนการตามสอบ Lot**: การจ้างผลิตภายนอกส่งของระหว่างผลิต
  // (WIP ของใบสั่งผลิต) ไปให้ผู้รับจ้างช่วงทำงานต่อแล้วรับกลับมาเป็นล็อตใหม่ — เป็น
  // เหตุการณ์ที่เกิดกลางสาย "ผลิต" และล็อตที่มันสร้างก็เป็นสิ่งที่การตามสอบ (กลุ่ม
  // ถัดไป) ตามรอยได้ การวางไว้ตรงกลางจึงตรงกับลำดับที่งานเกิดขึ้นจริง
  //
  // **กลุ่มของตัวเอง ไม่ใช่สมาชิกของกลุ่ม `production` หรือ `procurement`**:
  // `schemas.py` ฝั่งเซิร์ฟเวอร์เขียนไว้ตรง ๆ ว่า `OUTSOURCING` เป็นโมดูลสิทธิ์ของ
  // ตัวเอง เพราะการส่งงานออกไปให้ผู้รับจ้างช่วงไม่ใช่ทั้งการซื้อและการผลิตเอง —
  // เหตุผลเดียวกับที่ `planning` (D-093) และ `reporting` (D-106) ได้กลุ่มของตัวเอง
  {
    id: 'outsourcing',
    title: 'จ้างผลิตภายนอก',
    items: [
      {
        id: 'outsource-orders',
        label: 'ใบสั่งจ้างผลิตภายนอก (Outsource Order)',
        icon: '🛠️',
        availability: 'available',
        // path เดียวกับที่ `routes.ts` ลงทะเบียนไว้แล้ว (`OUTSOURCE_ORDERS_PATH`)
        // ห้ามมี path ที่สอง ไม่งั้นเมนูกับ guard จะชี้คนละเส้นและหลุดกันได้
        to: '/outsourcing/orders',
        moduleCode: 'OUTSOURCING',
        // `action` คือสิทธิ์ที่ต้องมีเพื่อ **เปิดหน้าจอนี้** ไม่ใช่สิทธิ์สูงสุดที่ทำ
        // อะไรได้บนหน้าจอ ตามกติกาเดียวกับทุกรายการที่เปิดใช้งานแล้ว:
        // `GET /v1/outsource-orders` ถูก gate ด้วย VIEW ฝั่งเซิร์ฟเวอร์ ส่วนการ
        // **เปิดใบ** (CREATE), **เพิ่มบรรทัด/ส่งออก/รับกลับ** (EDIT) และ
        // **ยืนยันพร้อมส่งออก/อนุมัติส่วนขาด** (APPROVE) ถูกบังคับ **ข้างใน**
        // `OutsourceOrderScreen` ตรงที่ของมันคือปุ่มแต่ละปุ่ม — ตรงกับด่านจริงฝั่ง
        // เซิร์ฟเวอร์ทีละเส้น (list/get = VIEW, create = CREATE, add-line/
        // dispatch/receive = EDIT, ready/approve-discrepancy = APPROVE)
        action: 'VIEW',
        note: 'ส่งวัตถุดิบ/งานระหว่างผลิตไปให้ผู้รับจ้างช่วง — ผู้รับจ้างช่วงเป็นที่เก็บของโรงงานเดียวกัน การส่งออกจึงไม่ใช่การขายและไม่โพสต์บัญชี ส่วนที่ขาดตอนรับกลับเท่านั้นที่ตัดบัญชี (D-103)',
      },
    ],
  },
  {
    id: 'traceability',
    title: 'การตามสอบ Lot',
    items: [
      {
        id: 'lot-traceability',
        label: 'ตามสอบ Lot (ย้อนกลับ / ไปข้างหน้า)',
        icon: '🔗',
        availability: 'available',
        // path เดียวกับที่ `routes.ts` ลงทะเบียนไว้แล้ว (`LOT_TRACEABILITY_PATH`)
        // ห้ามมี path ที่สอง ไม่งั้นเมนูกับ guard จะชี้คนละเส้นและหลุดกันได้
        to: '/traceability/lots',
        // **`INVENTORY` ไม่ใช่ `LOT_TRACEABILITY`** — D-073 ระบุว่า Phase 1 ไม่มี
        // โมดูล `LOT_TRACEABILITY` ให้ department ใดถือได้ และห้ามสร้างขึ้นใหม่
        // `GET /v1/lots/{lot_id}/genealogy/{backward,forward}` ถูก gate ด้วย
        // `require_permission("INVENTORY", "VIEW")` ฝั่งเซิร์ฟเวอร์ (ดู
        // `modules/inventory/router.py`) รายการนี้จึงประกาศด่านเดียวกับด่านจริง
        // — รายการที่ gate ด้วยโมดูลที่ไม่มีใครถือได้ คือเมนูที่ไม่มีใครเห็น
        moduleCode: 'INVENTORY',
        // อ่านอย่างเดียวทั้งหน้าจอ ไม่มีปุ่มที่เขียนข้อมูลให้กันด้วยสิทธิ์ที่แรงกว่า
        // (edge ของ `lot_genealogy` เขียนโดยธุรกรรม Scan-Out เท่านั้น — D-065)
        action: 'VIEW',
        note: 'ดูว่า lot นี้ทำมาจาก lot ไหนและไปอยู่ใน lot ไหนต่อ — ตรงหนึ่งก้าว ไล่ต่อได้ทีละก้าว (D-073)',
      },
      // ⚠️ สองรายการถัดจากนี้ยังเป็น `planned` และผูกกับโมดูล `LOT_TRACEABILITY`
      // ตามที่ M0 วางไว้ ก่อนที่ D-073 จะตัดสินว่า Phase 1 ไม่มีโมดูลนั้น และก่อนที่
      // จะชัดว่าสองทิศทางเป็นหน้าจอ **เดียวกัน** ไม่ใช่สองหน้า (ดู
      // `lotTraceability/LotTraceabilityScreen.tsx` — ทั้งสองทิศทางต้องเห็นพร้อมกัน
      // เพราะเหตุการณ์จริงหนึ่งครั้งถามทั้งสองคำถาม) การปลด/รวม/ถอดสองรายการนี้เป็น
      // การเปลี่ยนโครงเมนูที่ `docs/` และ `navigation.test.ts` ผูกอยู่ด้วย จึงเป็น
      // **ข้อเสนอให้ `erp-orchestrator`** ไม่ใช่การตัดสินใจของ Work Order นี้ —
      // ที่นี่จึงเพิ่มรายการที่ใช้งานได้จริงเข้ามาโดยไม่แตะของเดิม
      {
        id: 'genealogy-backward',
        label: 'สืบย้อนกลับ (Backward)',
        icon: '⏪',
        availability: 'planned',
        moduleCode: 'LOT_TRACEABILITY',
        action: 'VIEW',
        note: 'docs/TASKS.md M8',
      },
      {
        id: 'genealogy-forward',
        label: 'สืบไปข้างหน้า (Forward)',
        icon: '⏩',
        availability: 'planned',
        moduleCode: 'LOT_TRACEABILITY',
        action: 'VIEW',
        note: 'docs/TASKS.md M8 — ใช้ตอน recall',
      },
    ],
  },
  {
    id: 'accounting',
    title: 'บัญชีและต้นทุน',
    items: [
      {
        id: 'accounting-documents',
        label: 'เอกสารบัญชี',
        icon: '🧮',
        availability: 'planned',
        moduleCode: 'ACCOUNTING',
        action: 'VIEW',
        note: 'สร้างอัตโนมัติเท่านั้น ไม่มีปุ่มบันทึกตรง',
      },
      {
        id: 'order-variance',
        label: 'ผลต่างต้นทุนใบสั่งผลิต',
        icon: '📉',
        availability: 'planned',
        moduleCode: 'ACCOUNTING',
        action: 'VIEW',
        note: 'คำนวณตอนปิดใบสั่งผลิต (M5)',
      },
    ],
  },
  // `WO-P2-M10-005` — **กลุ่มใหม่กลุ่มที่สี่ของ Phase 2** (P2-10d, D-106)
  //
  // อยู่ **หลังบัญชีและก่อนระบบ**: ทั้งบัญชีและรายงานผู้บริหารเป็นชั้นที่สรุปตัวเลข
  // จากงานอื่นทั้งหมดในระบบ ไม่ใช่ต้นทางของธุรกรรมเอง การวางไว้ก่อนกลุ่ม `system`
  // (ซึ่งเป็นงานดูแลระบบ ไม่ใช่งานธุรกิจ) จึงยังคงลำดับ "งานธุรกิจก่อน งานระบบทีหลัง"
  // ของเมนูทั้งชุดไว้
  //
  // **กลุ่มของตัวเอง ไม่ใช่สมาชิกของกลุ่ม `accounting`**: `router.py` ฝั่งเซิร์ฟเวอร์
  // เขียนไว้ตรง ๆ ว่า reporting เป็นโดเมนใหม่ที่มีโมดูลสิทธิ์ของตัวเอง
  // (`EXECUTIVE_DASHBOARD`) ซึ่งเป็น**ข้อยกเว้นข้อแรกของ D-004** — ผู้ถือสิทธิ์นี้
  // เห็นทุกโรงงาน ต่างจากทุกโมดูลอื่นในกลุ่ม `accounting` ที่ยังกรองตาม plant scope
  // ตามปกติ การใส่ไว้กลุ่มเดียวกับบัญชีจะทำให้ข้อยกเว้นนี้ดูเหมือนเป็นเรื่องปกติของ
  // กลุ่มนั้น ทั้งที่มันคือสิทธิ์เดียวในทั้งระบบที่ทำแบบนี้
  {
    id: 'reporting',
    title: 'รายงานผู้บริหาร',
    items: [
      {
        id: 'executive-dashboard',
        label: 'ภาพรวมผู้บริหาร (Executive Dashboard)',
        icon: '📊',
        availability: 'available',
        // path เดียวกับที่ `routes.ts` ลงทะเบียนไว้แล้ว (`EXECUTIVE_DASHBOARD_PATH`)
        // ห้ามมี path ที่สอง ไม่งั้นเมนูกับ guard จะชี้คนละเส้นและหลุดกันได้
        to: '/reporting/executive-dashboard',
        // **`EXECUTIVE_DASHBOARD` โมดูลของตัวเอง ไม่ใช่ `ACCOUNTING` ที่ใช้ซ้ำ** —
        // นี่คือสิทธิ์เดียวที่ตั้งใจข้ามขอบเขตโรงงาน (D-106 ข้อ 4) การผูกกับโมดูลอื่น
        // จะทำให้ข้อยกเว้นนี้ปลดล็อกมาพร้อมสิทธิ์อื่นโดยไม่มีใครตั้งใจ — และ
        // ต้องไม่ถูกผูกกับ route ที่สอง (`router.py` assert เป็น equality)
        moduleCode: 'EXECUTIVE_DASHBOARD',
        // `action` คือสิทธิ์ที่ต้องมีเพื่อ **เปิดหน้าจอนี้** ตามกติกาเดียวกับทุก
        // รายการที่เปิดใช้งานแล้ว — และในกลุ่มนี้ยังเป็นสิทธิ์เดียวที่เคยมีด้วย:
        // ตารางนี้ไม่มีเส้นทางเขียนเลย (D-106 ข้อ 5) จึงไม่มีปุ่มใดให้ CREATE/EDIT/
        // APPROVE กันเพิ่มข้างในหน้าจอ
        action: 'VIEW',
        note: 'ตัวเลขที่ fold แล้วจากทุกโรงงาน สลับดูรายโรงงานหรือภาพรวมทั้งบริษัทได้ — อ่านอย่างเดียว ไม่มีปุ่มแก้ไขหรือคำนวณใหม่ (D-106)',
      },
    ],
  },
  {
    id: 'system',
    title: 'ระบบและสิทธิ์',
    items: [
      // ⚠️ `WO-P2-UI-001` แยก "แผนกและพนักงาน" หน้าเดียวออกเป็น **สองรายการ สอง
      // route สองหน้าจอ** — แผนกเป็นงานตั้งโครงองค์กรที่ทำนาน ๆ ครั้ง ส่วนการเพิ่ม
      // พนักงานและผูกสังกัดเป็นงานประจำ ทั้งคู่ยังอยู่ในกลุ่ม `system` เดิม ผูกกับ
      // โมดูล `RBAC_ADMIN` เดิม และ gate ด้วย VIEW เท่ากันตามกติกาของกลุ่มนี้
      //
      // `admin-departments` **คงรหัสเดิมและ path เดิมไว้** โดยตั้งใจ: ลิงก์/บุ๊กมาร์ก
      // ที่ชี้ `/system/departments` อยู่แล้วต้องไม่กลายเป็นเส้นที่ไม่มีอะไรอยู่
      {
        id: 'admin-departments',
        label: 'แผนก',
        icon: '🏢',
        availability: 'available',
        // path เดียวกับที่ `routes.ts` ลงทะเบียนไว้แล้ว ห้ามมี path ที่สอง
        // ไม่งั้นเมนูกับ guard จะชี้คนละเส้นและหลุดกันได้
        to: '/system/departments',
        moduleCode: 'RBAC_ADMIN',
        // `action` คือสิทธิ์ที่ต้องมีเพื่อ **เปิดหน้าจอนี้** ไม่ใช่สิทธิ์สูงสุดที่ทำ
        // อะไรได้บนหน้าจอ ตามกติกาเดียวกับทุกรายการที่เปิดแล้ว:
        // `GET /admin/departments` ถูก gate ด้วย VIEW ฝั่งเซิร์ฟเวอร์ ส่วนการ
        // **สร้างแผนก** ถูกกันด้วย `RBAC_ADMIN` + EDIT ข้างใน `DepartmentsScreen`
        // ซึ่งเป็นที่ของมัน
        action: 'VIEW',
        note: 'สร้างแผนกและดูรหัสแผนกที่ตารางสิทธิ์ผูกอยู่ — การผูกพนักงานเข้าแผนกอยู่ที่หน้า "พนักงาน"',
      },
      {
        id: 'admin-employees',
        label: 'พนักงาน',
        icon: '👤',
        availability: 'available',
        // path เดียวกับที่ `routes.ts` ลงทะเบียนไว้แล้ว ห้ามมี path ที่สอง
        to: '/system/employees',
        moduleCode: 'RBAC_ADMIN',
        // ด้วยเหตุผลเดียวกับรายการข้างบน: `GET /admin/employees` ถูก gate ด้วย VIEW
        // ฝั่งเซิร์ฟเวอร์ ส่วนการ **สร้างและผูก** ถูกกันด้วย EDIT ข้างใน
        // `EmployeesScreen`
        action: 'VIEW',
        note: 'สร้างพนักงาน และผูกเข้ากับแผนกหลักและโรงงานหลัก (อย่างละหนึ่ง)',
      },
      {
        id: 'admin-permissions',
        label: 'ตารางสิทธิ์ตามแผนก',
        icon: '🔐',
        availability: 'available',
        // path เดียวกับที่ `routes.ts` ลงทะเบียนไว้แล้ว ห้ามมี path ที่สอง
        // ไม่งั้นเมนูกับ guard จะชี้คนละเส้นและหลุดกันได้
        to: '/system/permissions',
        moduleCode: 'RBAC_ADMIN',
        // `action` คือสิทธิ์ที่ต้องมีเพื่อ **เปิดหน้าจอนี้** ไม่ใช่สิทธิ์สูงสุดที่ทำ
        // อะไรได้บนหน้าจอ เคยเป็น `EDIT` มาตั้งแต่ M0 ตอนที่ยังไม่มีหน้าจอ และเปลี่ยน
        // เป็น VIEW พร้อมกับการเปิดใช้งาน ตามกติกาเดียวกับทุกรายการที่เปิดแล้ว:
        // `GET /v1/admin/permissions` ถูก gate ด้วย VIEW ฝั่งเซิร์ฟเวอร์ ส่วนการ
        // **บันทึก** ถูกกันด้วย `RBAC_ADMIN` + EDIT ข้างใน `PermissionMatrixScreen`
        // ซึ่งเป็นที่ของมัน — สิทธิ์ที่แรงกว่าถูกบังคับที่หน้าจอเสมอในแอปนี้
        action: 'VIEW',
        note: 'department × action matrix — กลุ่ม System Administration ติ๊กไม่ได้ (D-060)',
      },
      {
        id: 'admin-roles',
        label: 'บทบาทระบบ (Admin / Auditor)',
        icon: '👑',
        availability: 'available',
        // path เดียวกับที่ `routes.ts` ลงทะเบียนไว้แล้ว ห้ามมี path ที่สอง
        // ไม่งั้นเมนูกับ guard จะชี้คนละเส้นและหลุดกันได้
        to: '/system/roles',
        moduleCode: 'RBAC_ADMIN',
        // `action` คือสิทธิ์ที่ต้องมีเพื่อ **เปิดหน้าจอนี้** ไม่ใช่สิทธิ์สูงสุดที่ทำ
        // อะไรได้บนหน้าจอ เคยเป็น `APPROVE` มาตั้งแต่ M0 ตอนที่ยังไม่มีหน้าจอ และ
        // เปลี่ยนเป็น VIEW พร้อมกับการเปิดใช้งาน ตามกติกาเดียวกับทุกรายการที่เปิดแล้ว:
        // `GET /v1/admin/roles` ถูก gate ด้วย VIEW ฝั่งเซิร์ฟเวอร์ ส่วนการ **มอบ/ถอด**
        // ถูกกันด้วย `RBAC_ADMIN` + EDIT ข้างใน `SystemRolesScreen` ซึ่งเป็นที่ของมัน
        // (`POST /v1/admin/roles` ก็ถูก gate ด้วย EDIT ไม่ใช่ APPROVE เช่นกัน —
        // ค่าเดิมจึงไม่ตรงกับด่านจริงฝั่งเซิร์ฟเวอร์ด้วยซ้ำ)
        action: 'VIEW',
        note: 'มอบ/ถอด ADMIN และ AUDITOR — ADMIN ตัดสิทธิ์เข้าถึงข้อมูลธุรกิจทั้งหมด (D-009)',
      },
      {
        id: 'admin-audit-log',
        label: 'บันทึกการเปลี่ยนสิทธิ์',
        icon: '📜',
        availability: 'available',
        // path เดียวกับที่ `routes.ts` ลงทะเบียนไว้แล้ว ห้ามมี path ที่สอง
        to: '/system/audit-log',
        moduleCode: 'RBAC_ADMIN',
        action: 'VIEW',
        note: 'คนละชุดกับ audit ข้อมูลธุรกิจ — อ่านอย่างเดียว ไม่มีเส้นทางเขียน',
      },
      {
        id: 'admin-sso-clients',
        label: 'แอปและ client SSO',
        icon: '🔑',
        availability: 'available',
        to: '/system/sso-clients',
        moduleCode: 'RBAC_ADMIN',
        action: 'VIEW',
        note: 'ลงทะเบียนแอป/role และ OIDC client — secret เห็นได้ครั้งเดียว (D-175)',
      },
      {
        id: 'admin-sso-app-roles',
        label: 'สิทธิ์เข้าแอปอื่น (SSO)',
        icon: '🪪',
        availability: 'available',
        to: '/system/sso-app-roles',
        moduleCode: 'RBAC_ADMIN',
        action: 'VIEW',
        note: 'ตั้ง/ถอน role ของพนักงานในแอปอื่น — ถอนแล้วออกจากระบบแอปนั้นทันที (D-175)',
      },
      {
        id: 'admin-plants',
        label: 'โรงงานและคลัง/ที่เก็บ',
        icon: '🏭',
        availability: 'available',
        to: '/system/plants',
        moduleCode: 'RBAC_ADMIN',
        action: 'VIEW',
        note: 'ดู/สร้าง/เปลี่ยนชื่อโรงงานและคลัง/ที่เก็บ — รหัสแก้ไม่ได้ ไม่มีลบ (D-176)',
      },
    ],
  },
];

/**
 * กลุ่มเมนูทั้งหมดตามลำดับที่ต้องเป็น — ใช้ยืนยันโครงใน automated test
 *
 * เจ็ดกลุ่มแรกเป็นโครงของ Phase 1 ส่วน `procurement` เป็นกลุ่มที่ **แปด** ที่
 * `WO-P2-M2-003` เพิ่มเข้ามา (P2-2a, D-084), `sales` เป็นกลุ่มที่ **เก้า** ที่
 * `WO-P2-M4-004` เพิ่ม (P2-4b, D-090), `planning` เป็นกลุ่มที่ **สิบ** ที่
 * `WO-P2-M5-003` เพิ่ม (P2-5a, D-093), `reporting` เป็นกลุ่มที่ **สิบเอ็ด** ที่
 * `WO-P2-M10-005` เพิ่ม (P2-10d, D-106) และ `outsourcing` เป็นกลุ่มที่ **สิบสอง**
 * ที่ `WO-P2-M8-007` เพิ่ม (P2-8g, D-103) ลำดับในรายการนี้คือลำดับที่เมนูต้องแสดงจริง
 * ไม่ใช่แค่ชุดของรหัส — เทสต์เทียบแบบ `toEqual` ไม่ใช่ `arrayContaining`
 *
 * `sales` แทรกอยู่ **ระหว่าง `procurement` กับ `planning`**, `planning` แทรกอยู่
 * **ระหว่าง `sales` กับ `inventory`** เพราะเมนูเรียงตามลำดับที่งานเกิดขึ้นจริง:
 * ความต้องการเข้ามา (ขาย) → วางแผนว่าจะทำอะไรกับมัน (วางแผน) → ของเคลื่อน (คลัง) —
 * `outsourcing` แทรกอยู่ **ระหว่าง `production` กับ `traceability`** (`WO-P2-M8-007`,
 * P2-8g, D-103) เพราะมันเป็นเหตุการณ์ที่เกิดกลางสายผลิต และ `reporting` แทรกอยู่
 * **ระหว่าง `accounting` กับ `system`** เพราะทั้งบัญชีและรายงานผู้บริหารเป็นชั้นที่
 * สรุปตัวเลขจากงานอื่นทั้งหมด ไม่ใช่ต้นทางธุรกรรมเอง
 */
export const REQUIRED_NAV_GROUP_IDS = [
  'home',
  'master-data',
  'procurement',
  'sales',
  'planning',
  'inventory',
  'production',
  'outsourcing',
  'traceability',
  'accounting',
  'reporting',
  'system',
] as const;

/** คำที่บ่งชี้ฟีเจอร์ Pull/Kanban ซึ่งห้าม enabled ใน Phase 1 */
export const PULL_KANBAN_PATTERN = /kanban|pull|คัมบัง|คัมบัน/i;

export function isNavItemEnabled(item: NavItem): boolean {
  return item.availability === 'available' && typeof item.to === 'string' && item.to !== '';
}

function splitPathAndSearch(to: string): { readonly path: string; readonly search: string } {
  const index = to.indexOf('?');
  return index === -1
    ? { path: to, search: '' }
    : { path: to.slice(0, index), search: to.slice(index) };
}

/**
 * แถวเมนูนี้ active อยู่หรือไม่ — เทียบทั้ง path **และ** search param (D-116 ข้อ 4)
 *
 * รายการทั่วไปที่ `to` ไม่มี query ยังเทียบแค่ path เหมือนที่ `Sidebar` เคยทำมาตลอด
 * ก่อน Work Order นี้ **ทุกประการ** — ไม่แตะพฤติกรรมของเมนูอื่นเลยแม้แต่ตัวเดียว
 * ส่วนที่ต้องเทียบ search เพิ่มคือรายการที่มี `children` ชี้ path เดียวกันด้วย
 * query ต่างกัน (`sales-orders` กับลูกของมัน) ไม่งั้นทั้งแม่และลูกจะ active
 * พร้อมกันเสมอทันทีที่ path ตรง
 */
export function isNavItemActive(item: NavItem, pathname: string, search: string): boolean {
  if (item.to === undefined) {
    return false;
  }
  const target = splitPathAndSearch(item.to);
  if (target.path !== pathname) {
    return false;
  }
  if (target.search !== '') {
    return target.search === search;
  }
  const childOwnsThisSearch = (item.children ?? []).some((child) => {
    if (child.to === undefined) {
      return false;
    }
    const childTarget = splitPathAndSearch(child.to);
    return childTarget.path === pathname && childTarget.search === search;
  });
  return !childOwnsThisSearch;
}

export function allNavItems(groups: readonly NavGroup[] = NAV_GROUPS): readonly NavItem[] {
  return groups.flatMap((group) => group.items);
}

export function isPullOrKanbanItem(item: NavItem): boolean {
  return PULL_KANBAN_PATTERN.test(`${item.id} ${item.label} ${item.note ?? ''}`);
}

export function availabilityLabel(availability: NavAvailability): string {
  switch (availability) {
    case 'available':
      return 'พร้อมใช้งาน';
    case 'planned':
      return 'วางแผนไว้';
    case 'phase-2':
      return 'เฟส 2';
  }
}

/**
 * รายการนี้ควรปรากฏในเมนูสำหรับสิทธิ์ชุดนี้หรือไม่ — **UX เท่านั้น**
 * การซ่อนลิงก์ไม่ได้ปกป้องข้อมูล backend ยังเป็นผู้ปฏิเสธ request จริงเสมอ
 *
 * - ไม่มี `moduleCode` (เช่น ภาพรวมระบบ) → แสดงเสมอ เพราะไม่ได้ผูกกับโมดูลใด
 * - มี `moduleCode` + `action` → แสดงเมื่อ `modules` ที่เซิร์ฟเวอร์ส่งมาติด flag นั้น
 * - มี `moduleCode` แต่ไม่มี `action` → ซ่อน (deny-safe: รายการที่บอกไม่ได้ว่า
 *   ต้องใช้สิทธิ์ระดับไหน ต้องไม่ถูกเดาให้ผ่าน)
 */
export function isNavItemVisible(state: PermissionsState, item: NavItem): boolean {
  if (item.moduleCode === undefined) {
    return true;
  }
  if (item.action === undefined) {
    return false;
  }
  return can(state, item.moduleCode, item.action);
}

/**
 * กลุ่มเมนูที่เหลือหลังกรองตามสิทธิ์
 *
 * กลุ่มที่ไม่เหลือรายการเลยถูกตัดทิ้งทั้งกลุ่ม ไม่ปล่อยหัวข้อว่างค้างไว้ —
 * หัวข้อว่างบอกใบ้ว่า "มีของอยู่ตรงนี้แต่คุณไม่เห็น" ซึ่งเป็นการรั่วโครงสร้าง
 * ระบบให้ผู้ที่ไม่มีสิทธิ์โดยไม่จำเป็น และอ่านเหมือนหน้าจอพัง
 *
 * ลูก (`children`) ถูกกรองด้วย `isNavItemVisible` เดียวกันนี้ด้วย (D-130) — ลูกที่
 * ไม่มี `moduleCode` ของตัวเอง (เช่น `sales-orders-receive`) ผ่านเสมอ จึงยังได้
 * สิทธิ์จากแม่เหมือนเดิมทุกประการ ส่วนลูกที่ประกาศ `moduleCode`/`action` ของตัวเอง
 * (เช่น `manual-plan`) ถูกซ่อนเมื่อผู้ใช้ไม่มีสิทธิ์นั้นแม้แม่จะแสดงอยู่
 *
 * รายการ/ลูกที่ `hidden: true` (D-131) ถูกตัดออกที่นี่ด้วยไม่ว่าจะมีสิทธิ์หรือไม่ —
 * `hidden` ไม่ใช่เรื่องสิทธิ์ จึงตรวจแยกจาก `isNavItemVisible`
 */
export function visibleNavGroups(
  state: PermissionsState,
  groups: readonly NavGroup[] = NAV_GROUPS,
): readonly NavGroup[] {
  return groups
    .map((group) => ({
      ...group,
      items: group.items
        .filter((item) => !item.hidden && isNavItemVisible(state, item))
        .map((item) =>
          item.children === undefined
            ? item
            : {
                ...item,
                children: item.children.filter(
                  (child) => !child.hidden && isNavItemVisible(state, child),
                ),
              },
        ),
    }))
    .filter((group) => group.items.length > 0);
}
